import { useState, type FormEvent } from "react";

import { useAuth } from "./AuthProvider";
import {
  validateEmailCode,
  validateNewPassword,
  validateSupervisorCredentials,
  validateSupervisorEmail,
} from "./auth";

import { BrandMark } from "../components/BrandMark";
import { resolveGuardWebSignInUrl } from "./guard-web-url";

export const GUARD_WEB_SIGN_IN_URL = resolveGuardWebSignInUrl();

type SignInMode = "password" | "code" | "reset";

/**
 * Supervisor sign-in form. Pre-network validation gives inline field hints; the sign-in call
 * itself surfaces only the SAFE generic failure. On success the AuthProvider flips the gate to
 * the shell — nothing to navigate here.
 */
export function SignIn() {
  const { requestEmailCode, requestPasswordReset, signIn, verifyEmailCode } = useAuth();
  const [mode, setMode] = useState<SignInMode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);

  function resetMessaging(nextMode: SignInMode) {
    setMode(nextMode);
    setError(null);
    setNotice(null);
    setCodeSent(false);
    setCode("");
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);

    if (mode === "code") {
      if (!codeSent) {
        const validation = validateSupervisorEmail(email);
        if (!validation.ok) {
          setError(validation.reason);
          return;
        }
        setBusy(true);
        const result = await requestEmailCode(email);
        setBusy(false);
        if (!result.ok) {
          setError(result.reason);
          return;
        }
        setCodeSent(true);
        setNotice(result.message);
        return;
      }

      const codeValidation = validateEmailCode(code);
      if (!codeValidation.ok) {
        setError(codeValidation.reason);
        return;
      }
      setBusy(true);
      const result = await verifyEmailCode({ email, code });
      setBusy(false);
      if (!result.ok) {
        setError(result.reason);
      }
      return;
    }

    if (mode === "reset") {
      const validation = validateSupervisorEmail(email);
      if (!validation.ok) {
        setError(validation.reason);
        return;
      }
      setBusy(true);
      const result = await requestPasswordReset(email);
      setBusy(false);
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      setNotice(result.message);
      return;
    }

    const validation = validateSupervisorCredentials({ email, password });
    if (!validation.ok) {
      setError(validation.reason);
      return;
    }

    setBusy(true);
    const result = await signIn({ email, password });
    setBusy(false);
    if (!result.ok) {
      setError(result.reason);
    }
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={handleSubmit} aria-labelledby="signin-title">
        <header className="signin__brand">
          <BrandMark className="signin__mark" />
          <p className="signin__eyebrow">SecOps</p>
          <h1 id="signin-title" className="signin__title">
            Command Center
          </h1>
          <p className="signin__subtitle">Supervisor sign-in</p>
        </header>

        <div className="signin__tabs" aria-label="Sign-in method">
          <button
            type="button"
            className={mode === "password" ? "signin__tab signin__tab--active" : "signin__tab"}
            onClick={() => resetMessaging("password")}
            disabled={busy}
          >
            Password
          </button>
          <button
            type="button"
            className={mode === "code" ? "signin__tab signin__tab--active" : "signin__tab"}
            onClick={() => resetMessaging("code")}
            disabled={busy}
          >
            Email code
          </button>
        </div>

        <label className="signin__field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={busy}
            required
          />
        </label>

        {mode === "password" ? (
          <label className="signin__field">
            <span>Password</span>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={busy}
              required
            />
          </label>
        ) : null}

        {mode === "code" && codeSent ? (
          <label className="signin__field">
            <span>6-digit code</span>
            <input
              type="text"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={busy}
              required
            />
          </label>
        ) : null}

        {error ? (
          <p className="signin__error" role="alert">
            {error}
          </p>
        ) : null}

        {notice ? <p className="signin__notice">{notice}</p> : null}

        <button className="signin__submit" type="submit" disabled={busy}>
          {submitLabel(mode, busy, codeSent)}
        </button>

        <div className="signin__links">
          {mode === "reset" ? (
            <button type="button" className="signin__textbtn" onClick={() => resetMessaging("password")}>
              Back to sign in
            </button>
          ) : (
            <button type="button" className="signin__textbtn" onClick={() => resetMessaging("reset")}>
              Forgot password?
            </button>
          )}
          <a href={GUARD_WEB_SIGN_IN_URL} className="signin__guardlink">
            Guard Web app
          </a>
        </div>
      </form>
    </div>
  );
}

export function PasswordReset() {
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    const validation = validateNewPassword(password);
    if (!validation.ok) {
      setError(validation.reason);
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    const result = await updatePassword(password);
    setBusy(false);
    if (!result.ok) {
      setError(result.reason);
      return;
    }
    setPassword("");
    setConfirm("");
    setNotice("Password updated. You are signed in.");
  }

  return (
    <div className="signin">
      <form className="signin__card" onSubmit={handleSubmit} aria-labelledby="reset-title">
        <header className="signin__brand">
          <BrandMark className="signin__mark" />
          <p className="signin__eyebrow">SecOps</p>
          <h1 id="reset-title" className="signin__title">
            Reset password
          </h1>
          <p className="signin__subtitle">Choose a new SecOps console password.</p>
        </header>

        <label className="signin__field">
          <span>New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={busy}
            required
          />
        </label>
        <label className="signin__field">
          <span>Confirm password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            disabled={busy}
            required
          />
        </label>

        {error ? (
          <p className="signin__error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="signin__notice">{notice}</p> : null}

        <button className="signin__submit" type="submit" disabled={busy}>
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </div>
  );
}

function submitLabel(mode: SignInMode, busy: boolean, codeSent: boolean): string {
  if (busy) {
    if (mode === "reset") return "Sending…";
    if (mode === "code" && !codeSent) return "Sending…";
    return "Signing in…";
  }
  if (mode === "reset") return "Send reset link";
  if (mode === "code") return codeSent ? "Verify code" : "Send code";
  return "Sign in";
}
